import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ChallengeDto } from '@qc/shared/dto';
import { ChallengeType } from '@qc/shared/enums';
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { AuthChallengeService } from './auth-challenge.service';
import { ChallengeStorageService } from './challenge-storage.service';
import { TokenStorageService } from './token-storage.service';
import { ChallengeTimerService } from './challenge-timer.service';

export interface ChallengeResult {
    accessToken?: string | null;
    refreshToken?: string | null;
    authChallengeToken?: string | null;
    challenge?: ChallengeDto;
    otpAuthUri?: string;
    secretKey?: string;
}

@Injectable({ providedIn: 'root' })
export class ChallengeProcessorService {
    readonly #authChallengeService = inject(AuthChallengeService);
    readonly #challengeStorage = inject(ChallengeStorageService);
    readonly #tokenStorage = inject(TokenStorageService);
    readonly #router = inject(Router);
    readonly #challengeTimerService = inject(ChallengeTimerService);

    processChallenge(result: ChallengeResult, updateToken: boolean = false): Observable<ChallengeResult> {
        if (result.accessToken && result.refreshToken) {
            this.#challengeStorage.clearChallenge();
            this.#tokenStorage.saveTokens(result.accessToken, result.refreshToken);
            setTimeout(() => {
                this.#router.navigate(['/']);
            }, 100);
            return of(result);
        }

        if (result.challenge && result.authChallengeToken) {
            this.#challengeStorage.saveChallenge({
                authChallengeToken: result.authChallengeToken,
                challenge: result.challenge,
                challengeType: result.challenge.type,
                challengeReason: result.challenge.reason,
                challengeDelivery: result.challenge.delivery,
                details: result.challenge.details,
            });

            this.#challengeTimerService.resetTimer();

            this.routeToChallenge(result.challenge.type);
        } else if (result.authChallengeToken && updateToken) {
            this.#challengeStorage.saveChallenge({
                authChallengeToken: result.authChallengeToken,
            });
            this.#challengeTimerService.resetTimer();
        } else if (result.authChallengeToken) {
            this.#challengeStorage.saveChallenge({
                authChallengeToken: result.authChallengeToken,
                challengeType: null,
                challengeReason: null,
                challengeDelivery: null,
            });

            this.#challengeTimerService.resetTimer();
        } else {
            this.#router.navigate(['/']);
        }

        return of(result);
    }

    routeToChallenge(challengeType: string): void {
        if (!challengeType) {
            console.warn('Attempted to route to a challenge with no type specified');
            return;
        }
        const type = challengeType.toUpperCase();

        switch (type) {
            case ChallengeType.MFA.toUpperCase():
                this.#router.navigate(['/auth/mfa-verify']);
                break;
            case ChallengeType.MFA_CONFIG.toUpperCase():;
                this.#router.navigate(['/auth/mfa-setup']);
                break;
            case ChallengeType.DEVICE_VERIFICATION.toUpperCase():
                this.#router.navigate(['/auth/device-verify']);
                break;
            case ChallengeType.PASSWORD_CHANGE.toUpperCase():
                this.#router.navigate(['/auth/password-change']);
                break;
            default:
                this.#router.navigate(['/profile']);
        }
    }

    submitChallenge<T, R>(action: (request: T) => Observable<{ data: R }>, request: T): Observable<ChallengeResult> {
        return action.call(this.#authChallengeService, request).pipe(
            tap((response) => console.log('Challenge response:', response?.data)),
            switchMap(({ data }) => {
                const result = data as unknown as ChallengeResult;
                return this.processChallenge(result);
            }),
        );
    }
}
